import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_API_URL || '';

// ── DEV UI BYPASS ─────────────────────────────────────────────────
// Set to true to skip login and preview the full UI as Super Admin.
// Set back to false before a production build.
const DEV_BYPASS = false; // true;

const DEV_USER = {
  id: 0,
  name: 'TMIS Admin',
  email: '',
  role: 'super_admin',
};

const DEV_PERMISSIONS = ['home','students','records','reports','sessions','users','permissions','audit'].reduce((acc, m) => {
  acc[m] = { view: true, create: true, update: true, delete: true };
  return acc;
}, {});
// ─────────────────────────────────────────────────────────────────

export function AuthProvider({ children }) {
  const [user, setUser] = useState(DEV_BYPASS ? DEV_USER : null);
  const [permissions, setPermissions] = useState(DEV_BYPASS ? DEV_PERMISSIONS : {});
  const [loading, setLoading] = useState(!DEV_BYPASS);
  const [token, setToken] = useState(() => DEV_BYPASS ? 'dev-bypass' : localStorage.getItem('tmis_token'));

  // Axios default header
  useEffect(() => {
    if (token && !DEV_BYPASS) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else if (!DEV_BYPASS) {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const fetchMe = useCallback(async (tk) => {
    try {
      const res = await axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      if (res.data.success) {
        setUser(res.data.user);
        setPermissions(res.data.permissions || {});
      } else {
        logout();
      }
    } catch {
      logout();
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (DEV_BYPASS) return;
    if (token) {
      fetchMe(token);
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  function login(newToken, userData, perms) {
    localStorage.setItem('tmis_token', newToken);
    setToken(newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setUser(userData);
    setPermissions(perms || {});
  }

  function logout() {
    if (DEV_BYPASS) return; // prevent logout in bypass mode
    localStorage.removeItem('tmis_token');
    setToken(null);
    setUser(null);
    setPermissions({});
    delete axios.defaults.headers.common['Authorization'];
    setLoading(false);
  }

  function can(module, action = 'view') {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return !!(permissions[module]?.[action]);
  }

  return (
    <AuthContext.Provider value={{ user, permissions, token, loading, login, logout, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
